import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { investmentService } from '@services/investmentService';
import toast from 'react-hot-toast';

const QUERY_KEY = 'investments';

export const useInvestments = () => {
  const queryClient = useQueryClient();

  const {
    data: investments = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: investmentService.getAll,
  });

  const createMutation = useMutation({
    mutationFn: investmentService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Investimento criado com sucesso!');
    },
    onError: (error) => {
      const fieldErrors = error.response?.data?.error?.errors;
      if (fieldErrors?.length > 0) {
        const msg = fieldErrors.map(e => e.message).join(' • ');
        toast.error(msg);
      } else {
        toast.error(error.response?.data?.error?.message || 'Erro ao criar investimento');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ investmentId, data }) => investmentService.update(investmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Investimento atualizado!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao atualizar');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: investmentService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Investimento excluído!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao excluir');
    },
  });

  const contributionMutation = useMutation({
    mutationFn: ({ investmentId, data }) => investmentService.addContribution(investmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Movimentação registrada!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao registrar');
    },
  });

  // Calcular totais
  const totalInvested = investments.reduce((sum, i) => sum + (i.totalContributions || 0), 0);
  const totalCurrentValue = investments.reduce((sum, i) => sum + (i.projectedCurrentValue || i.currentAmount || 0), 0);
  const totalEarnings = totalCurrentValue - totalInvested;

  return {
    investments,
    totalInvested,
    totalCurrentValue,
    totalEarnings,
    returnPercentage: totalInvested > 0 ? (totalEarnings / totalInvested) * 100 : 0,
    isLoading,
    error,
    refetch,
    createInvestment: createMutation.mutateAsync,
    updateInvestment: updateMutation.mutateAsync,
    deleteInvestment: deleteMutation.mutateAsync,
    addContribution: contributionMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

export const useInvestmentDetail = (investmentId) => {
  return useQuery({
    queryKey: [QUERY_KEY, investmentId],
    queryFn: () => investmentService.getById(investmentId),
    enabled: !!investmentId,
  });
};

export const useInvestmentSimulation = () => {
  return useMutation({
    mutationFn: investmentService.simulate,
  });
};

export const useInvestmentSummary = () => {
  return useQuery({
    queryKey: [QUERY_KEY, 'summary'],
    queryFn: investmentService.getSummary,
  });
};

export default useInvestments;
